import { cx } from '@emotion/css';
import { type FC, type PropsWithChildren, Children, useId, useRef } from 'react';

import { Alignment, BackgroundColor } from '../../constants';
import { useIsMobile } from '../../hooks';
import { MotifComponent, useMotifStyles } from '../../motif';
import { getMotifSchemeForLegacyBackgroundColor } from '../../motif/motifUtils';
import { BlockBoundary } from '../BlockBoundary';
import {
  boundaryCss,
  cardsColumnCss,
  cardWrapperCss,
  contentWrapperCss,
  contentWrapperStartAlignmentCss,
  eyebrowCss,
  immersiveScrollBlockCss,
  navigationElementActiveCss,
  navigationElementCss,
  navigationWrapperAlignmentCss,
  navigationWrapperCss,
  textCenterCss,
  textColumnCss,
  textColumnStartAlignmentCss,
  textCss,
  textEndCss,
  textStartCss,
  textWrapperCss,
  titleCss,
} from './styles';
import type { ImmersiveScrollBlockProps } from './types';
import { useActiveCardObserver } from './useActiveCardObserver';
import { useWheelScrollAnimation } from './useWheelScrollAnimation';

export const ImmersiveScrollBlock: FC<PropsWithChildren<ImmersiveScrollBlockProps>> = props => {
  const {
    anchor,
    backgroundColor = BackgroundColor.Black,
    children,
    eyebrow,
    text,
    textAlignmentMobile = Alignment.Center,
    tilesDirection = Alignment.End,
    title,
  } = props;
  // Using Carousel motif because children from Web component are CarouseLCards.
  // TODO: Find a way to not couple this component with Motif variables.
  useMotifStyles(MotifComponent.CAROUSEL);
  const cardRefs = useRef<Array<HTMLLIElement | null>>([]);
  const containerRef = useRef<HTMLElement>(null);
  const childCount = Children.count(children);
  const isMobile = useIsMobile();
  const titleId = useId();

  const setCardRef = (el: HTMLLIElement | null, index: number) => {
    cardRefs.current[index] = el;
  };

  useWheelScrollAnimation({ elementRefs: cardRefs, childCount, isEnabled: isMobile });
  const activeCardIndex = useActiveCardObserver({
    cardRefs,
    childCount,
    options: {
      isEnabled: !isMobile,
      threshold: 0.8,
    },
  });

  return (
    <section
      aria-labelledby={titleId}
      className={cx(
        immersiveScrollBlockCss,
        getMotifSchemeForLegacyBackgroundColor(backgroundColor)
      )}
      id={anchor}
      ref={containerRef}
    >
      <BlockBoundary className={boundaryCss}>
        <div
          className={cx(contentWrapperCss, {
            [contentWrapperStartAlignmentCss]: tilesDirection === Alignment.Start,
          })}
        >
          <section
            className={cx(textColumnCss, {
              [textColumnStartAlignmentCss]: tilesDirection === Alignment.Start,
            })}
          >
            <ul
              className={cx(navigationWrapperCss, {
                [navigationWrapperAlignmentCss]: tilesDirection === Alignment.Start,
              })}
            >
              {Children.map(children, (_, index) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: Ok if list is static
                  key={index}
                >
                  <button
                    type="button"
                    aria-label={`Go to item ${index + 1}`}
                    onClick={() => {
                      cardRefs.current[index]?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={cx(navigationElementCss(backgroundColor), {
                      [navigationElementActiveCss(backgroundColor)]: activeCardIndex === index,
                    })}
                    aria-current={activeCardIndex === index ? 'true' : 'false'}
                  />
                </li>
              ))}
            </ul>
            <div
              className={cx(textWrapperCss, {
                [textStartCss]: textAlignmentMobile === Alignment.Start,
                [textCenterCss]: textAlignmentMobile === Alignment.Center,
                [textEndCss]: textAlignmentMobile === Alignment.End,
              })}
            >
              {eyebrow && <div className={eyebrowCss}>{eyebrow}</div>}
              {
                <h2 id={titleId} className={titleCss}>
                  {title}
                </h2>
              }
              {text && <div className={textCss}>{text}</div>}
            </div>
          </section>
          <ul className={cardsColumnCss}>
            {Children.map(children, (child, index) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: Ok to use index
                key={index}
                className={cardWrapperCss}
                ref={el => setCardRef(el, index)}
              >
                {child}
              </li>
            ))}
          </ul>
        </div>
      </BlockBoundary>
    </section>
  );
};

ImmersiveScrollBlock.displayName = 'ImmersiveScrollBlock';
